/**
 * Route guards for the admin area. Every admin page is gated on the
 * PERMISSION it needs (`RequirePermission`, matching its nav item in
 * AppShell); the platform-global pages (scope catalog, modules) stay
 * SUPER_ADMIN role-gated. A caller who fails a gate is redirected to chat.
 *
 * This is UX/navigation gating — the API enforces authorization on every
 * request regardless. While `/api/me` is in flight we render nothing rather
 * than flash the wrong screen.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useMe } from '../hooks/useMe.js';
import { isSuperAdmin } from '../lib/roles.js';
import { canOpenReports, hasAnyPermission, type Permission } from '../lib/permissions.js';

import { Skeleton } from '@servicecompressionllc/sc-design-system';

/** Sized like a page: a bare "Loading…" sentence read as a broken screen on
 *  every hard navigation. (The word stays for tests + screen readers.) */
const Pending = () => (
  <div className="p-8" aria-busy="true">
    <span className="sr-only">Loading…</span>
    <Skeleton className="mb-4 h-8 w-64" />
    <Skeleton className="h-48 w-full" />
  </div>
);

/** Admit a user holding ANY of the named permissions (mirrors the API's
 *  any-of `requirePermission` guard). */
export const RequirePermission = ({
  anyOf,
  children,
}: {
  anyOf: readonly Permission[];
  children: ReactNode;
}) => {
  const me = useMe();
  if (me.isLoading) return <Pending />;
  if (!me.data || !hasAnyPermission(me.data.user, anyOf)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/** The Reports pages: Reports.Read or Reports.Write, the same scopes the API checks. */
export const RequireReports = ({ children }: { children: ReactNode }) => {
  const me = useMe();
  if (me.isLoading) return <Pending />;
  if (!me.data || !canOpenReports(me.data.user)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

export const RequireSuperAdmin = ({ children }: { children: ReactNode }) => {
  const me = useMe();
  if (me.isLoading) return <Pending />;
  if (!me.data || !isSuperAdmin(me.data.user.roles)) return <Navigate to="/" replace />;
  return <>{children}</>;
};
